import type {
  MarketPrice,
  NewSealedProduct,
  SealedProduct,
  SealedProductListFilter,
  SealedStockUpdate,
} from "@hobbyshopify/backend-client";
import type { Result } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadReviewQueue } from "../../core/ops/review-ops";
import {
  archiveSealedProduct,
  createSealedProduct,
  loadSealedMarketPrice,
  loadSealedProducts,
  refreshSealedMarketPrice,
  setSealedStock,
} from "../../core/ops/sealed-ops";
import { idleMarketRefresh } from "../../core/primitives/catalog-market";
import { errorText } from "../../core/primitives/errors";
import { Loader } from "../../core/primitives/loader";
import { reviewWaiting } from "../../core/primitives/review-row";
import { SEALED_SAVE_FAILED_ON_THE_WAY } from "../../core/primitives/sealed-product";
import { SealedActions, type SealedDialog, SealedState } from "./sealed-model";

export type SealedDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository" | "reviewQueueRepository">;

interface SealedQuery {
  readonly shopId: string;
  readonly filter: SealedProductListFilter;
  readonly search: string;
  /** Numbered when asked: a row a save holds is freed by the first read asked after the save to land. */
  readonly read: number;
}

/** The shop's sealed product: what it breaks, counted the same way its cards are. */
export class SealedController extends SubtreeController implements SealedActions {
  readonly state = new SealedState();
  readonly #list: Loader<SealedQuery, SealedProduct[]>;
  /** Reopening for another product supersedes a load still in flight. */
  readonly #market: Loader<{ shopId: string; sealedProductId: string }, MarketPrice>;
  #shopId: string | null = null;
  #asked = 0;
  #landed = 0;
  /** Each held row and the read asked after its save; a row still being saved waits for no read yet. */
  readonly #holds = new Map<string, number>();
  /** The read a thrown save's dialog waits for: its Save is free once that read answers or a newer one lands. */
  #checkAfter: number | null = null;

  constructor(readonly deps: SealedDeps) {
    super();
    this.subtree.put(SealedState, this.state);
    this.subtree.put(SealedActions, this);
    this.#list = this.own(
      new Loader({
        status: this.state.status,
        fetch: (query: SealedQuery) => loadSealedProducts({ ...deps, ...query }),
        loaded: (items, query) => {
          this.state.items.value = items;
          this.state.answered.value = true;
          this.#landed = query.read;
          this.#free();
        },
      }),
    );
    this.#market = this.own(
      new Loader({
        status: this.state.marketStatus,
        fetch: (o: { shopId: string; sealedProductId: string }) => loadSealedMarketPrice({ ...deps, ...o }),
        loaded: (market) => {
          this.state.market.value = market;
        },
      }),
    );
    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId === this.#shopId) return;
      this.#shopId = shopId;
      this.state.items.value = [];
      this.state.answered.value = false;
      void this.#reload();
    }, [deps.storeRepository]);
    this.sync(() => {
      const repo = deps.reviewQueueRepository;
      this.state.reviewCount.value = reviewWaiting(repo.groups, new Date());
      const shopId = deps.storeRepository.shopId;
      // A badge's read: one that fails is said on Review, not here.
      if (shopId !== null && (repo.status.kind === "idle" || repo.isStale))
        void loadReviewQueue({ ...deps, shopId, locationId: deps.storeRepository.locationId }).catch(() => undefined);
    }, [deps.reviewQueueRepository, deps.storeRepository]);
  }

  setFilter = (filter: SealedProductListFilter) => {
    this.state.filter.value = filter;
    void this.#reload();
  };

  setSearch = (search: string) => {
    this.state.search.value = search;
    const query = this.#query();
    if (!query) return;
    if (search) this.#list.loadDebounced(query);
    else void this.#list.load(query);
  };

  openAdd = () => this.#open({ kind: "add" });
  openStock = (product: SealedProduct) => this.#open({ kind: "stock", product });
  openArchive = (product: SealedProduct) => this.#open({ kind: "archive", product });

  openMarket = async (product: SealedProduct) => {
    const shopId = this.#shopId;
    if (shopId === null) return;
    this.#open({ kind: "market", product });
    this.state.market.value = null;
    this.state.marketRefresh.value = idleMarketRefresh;
    await this.#market.load({ shopId, sealedProductId: product.id });
  };

  refreshMarket = async () => {
    const dialog = this.state.dialog.value;
    const shopId = this.#shopId;
    if (dialog?.kind !== "market" || shopId === null || this.state.marketRefresh.value.kind === "busy") return;
    this.state.marketRefresh.value = { kind: "busy" };
    const sealedProductId = dialog.product.id;
    const r = await refreshSealedMarketPrice({ ...this.deps, shopId, sealedProductId });
    if (this.isDisposed || this.state.dialog.value !== dialog) return;
    if (!r.ok) {
      this.state.marketRefresh.value = { kind: "error", message: errorText(r.errors) };
      return;
    }
    this.state.marketRefresh.value = { kind: "done", outcome: r.value.outcome };
    // The history gained a point, and the row's estimate moved with it.
    await this.#market.load({ shopId, sealedProductId });
    await this.#reload();
  };

  /** Held while its save is on its way, as its buttons are: the answer still lands in it. */
  closeDialog = () => {
    if (this.state.dialogBusy.value) return;
    this.state.dialog.value = null;
    this.#market.reset();
  };

  saveNew = async (product: NewSealedProduct) => {
    await this.#runDialog("add", (shopId) => createSealedProduct({ ...this.deps, shopId, product }));
  };

  saveStock = async (change: SealedStockUpdate) => {
    await this.#runDialog("stock", (shopId, product) =>
      setSealedStock({ ...this.deps, shopId, sealedProductId: (product as SealedProduct).id, change }),
    );
  };

  confirmArchive = async () => {
    await this.#runDialog("archive", (shopId, product) =>
      archiveSealedProduct({ ...this.deps, shopId, sealedProductId: (product as SealedProduct).id }),
    );
  };

  /** A dialog opened while a thrown save's list read is out says why its Save waits. */
  #open(dialog: SealedDialog): void {
    this.state.dialogError.value = this.state.dialogChecking.value ? SEALED_SAVE_FAILED_ON_THE_WAY : null;
    this.state.dialog.value = dialog;
  }

  async #runDialog(
    kind: SealedDialog["kind"],
    action: (shopId: string, product: SealedProduct | null) => Promise<Result<unknown, Error>>,
  ): Promise<void> {
    const dialog = this.state.dialog.value;
    const shopId = this.#shopId;
    const { dialogBusy, dialogChecking } = this.state;
    if (!dialog || dialog.kind !== kind || shopId === null || dialogBusy.value || dialogChecking.value) return;
    const product = dialog.kind === "add" ? null : dialog.product;
    if (product) this.#hold(product.id, Number.POSITIVE_INFINITY);
    dialogBusy.value = true;
    this.state.dialogError.value = null;
    let r: Result<unknown, Error>;
    try {
      r = await action(shopId, product);
    } catch (thrown) {
      // It may have gone through: said in the dialog, the list read again before its Save is free, and thrown on.
      if (!this.isDisposed) {
        dialogBusy.value = false;
        const read = this.#asked + 1;
        this.#checkAfter = read;
        dialogChecking.value = true;
        this.state.dialogError.value = SEALED_SAVE_FAILED_ON_THE_WAY;
        await this.#readAfter(product).catch(() => undefined);
        if (this.#checkAfter === read) this.#endCheck();
      }
      throw thrown;
    }
    if (this.isDisposed) return;
    // The dialog is free once its save answers: a list read that is slow, or never answers, holds only the row.
    dialogBusy.value = false;
    if (!r.ok) {
      if (product) this.#release(product.id);
      this.state.dialogError.value = errorText(r.errors);
      return;
    }
    this.state.dialog.value = null;
    await this.#readAfter(product);
  }

  /** The list read again after a save; the saved row is held until a read asked after it has landed. */
  async #readAfter(product: SealedProduct | null): Promise<void> {
    const read = this.#asked + 1;
    if (product) this.#hold(product.id, read);
    await this.#reload();
  }

  #hold(id: string, read: number): void {
    this.#holds.set(id, read);
    this.state.held.value = new Set(this.#holds.keys());
  }

  #release(id: string): void {
    if (!this.#holds.delete(id)) return;
    this.state.held.value = new Set(this.#holds.keys());
  }

  #free(): void {
    for (const [id, read] of this.#holds) if (read <= this.#landed) this.#holds.delete(id);
    this.state.held.value = new Set(this.#holds.keys());
    if (this.#checkAfter !== null && this.#checkAfter <= this.#landed) this.#endCheck();
  }

  #endCheck(): void {
    this.#checkAfter = null;
    this.state.dialogChecking.value = false;
  }

  retry = () => {
    void this.#reload();
  };

  async #reload(): Promise<void> {
    const query = this.#query();
    if (query) await this.#list.load(query);
  }

  #query(): SealedQuery | null {
    if (this.#shopId === null) return null;
    this.#asked += 1;
    return {
      shopId: this.#shopId,
      filter: this.state.filter.value,
      search: this.state.search.value,
      read: this.#asked,
    };
  }
}
